import dayjs from "dayjs";
import { Pencil, Trash2 } from "lucide-react";

const HEADERS = [
  { label: "Payment ID", className: "w-40" },
  { label: "Type", className: "w-52" },
  { label: "Details", className: "" },
  { label: "Created At", className: "w-40" },
  { label: "Actions", className: "w-24" },
];

const EMAIL_METHODS = ["paypal", "payoneer", "skrill"];

const paymentCode = (id) => `PMT-${String(id).padStart(3, "0")}`;

const TYPE_LABELS = {
  bank_transfer: "Bank Transfer",
  paypal: "PayPal",
  payoneer: "Payoneer",
  skrill: "Skrill",
  other: "Other",
};

// Status reads off the row itself - a coloured rail down the left edge plus a
// dot beside the payment id - so it needs no column of its own.
const StatusIndicator = ({ active }) => (
  <span className="inline-flex items-center gap-1.5">
    <span
      className={`w-1.5 h-1.5 rounded-full ${
        active ? "bg-emerald-500" : "bg-gray-300"
      }`}
    />
    <span
      className={`text-[10px] font-semibold uppercase tracking-wide ${
        active ? "text-emerald-600" : "text-gray-400"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  </span>
);

const DefaultBadge = () => (
  <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-brand-50 text-brand-700">
    Default
  </span>
);

const formatDate = (dateString) =>
  dateString ? dayjs(dateString).format("YYYY-MM-DD HH:mm") : "—";

// Every field for the method, laid out in full - no inner scrollbox.
const detailFields = (method) => {
  const type = method.payment_method;

  if (type === "bank_transfer") {
    const d = method.bank_transfer_detail || {};
    return [
      { label: "Payment Method Name", value: d.payment_method_name },
      { label: "Beneficiary Name", value: d.beneficiary_name },
      { label: "Beneficiary Address", value: d.beneficiary_address },
      { label: "Bank Name", value: d.bank_name },
      { label: "Bank Address", value: d.bank_address },
      { label: "Account Number", value: d.account_number },
      { label: "IFSC Code", value: d.ifsc_code },
      { label: "SWIFT", value: d.swift },
      { label: "IBAN", value: d.iban },
      { label: "Sort Code", value: d.sort_code },
      { label: "Country", value: d.country },
      { label: "State/Province/Region", value: d.state_region },
      { label: "City", value: d.city },
      { label: "Postal/ZIP Code", value: d.postal_code },
    ];
  }

  if (EMAIL_METHODS.includes(type)) {
    const d = method.email_payment_detail || {};
    return [
      { label: "Email", value: d.email },
      { label: "Account Holder Name", value: d.account_holder_name },
    ];
  }

  if (type === "other") {
    const d = method.other_payment_detail || {};
    return [{ label: "Account Holder Name", value: d.account_holder_name }];
  }

  return [];
};

const Details = ({ method }) => {
  const fields = detailFields(method).filter(
    (f) => f.value && String(f.value).trim() !== ""
  );

  if (fields.length === 0) return <span className="text-gray-400">—</span>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1 text-[11px] text-left">
      {fields.map((f) => (
        <div key={f.label} className="flex gap-1.5">
          <span className="text-gray-500 shrink-0">{f.label}:</span>
          <span className="text-gray-800 break-words">{f.value}</span>
        </div>
      ))}
    </div>
  );
};

const PaymentMethodList = ({ methods, onEdit, onDelete }) => {
  return (
    <div className="w-full bg-white shadow rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse min-w-[900px]">
          <thead>
            <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
              {HEADERS.map((h) => (
                <th
                  key={h.label}
                  className={`px-3 py-2 text-left font-semibold whitespace-nowrap ${h.className}`}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {methods.length === 0 ? (
              <tr>
                <td
                  colSpan={HEADERS.length}
                  className="py-10 text-center text-gray-400 text-xs"
                >
                  No payment methods added yet.
                </td>
              </tr>
            ) : (
              methods.map((method) => {
                const active = method.active_flag;
                const isOther = method.payment_method === "other";

                return (
                  <tr
                    key={method.id}
                    className={`border-b last:border-b-0 border-l-4 hover:bg-brand-50/50 transition-colors ${
                      active
                        ? "border-l-emerald-400 bg-white"
                        : "border-l-gray-200 bg-gray-50/60"
                    }`}
                  >
                    <td className="px-3 py-3 align-top">
                      <div className="text-xs font-medium text-gray-900">
                        {paymentCode(method.id)}
                      </div>
                      <div className="mt-1">
                        <StatusIndicator active={active} />
                      </div>
                    </td>

                    <td className="px-3 py-3 align-top">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span
                          className={`text-xs ${
                            active ? "text-gray-900" : "text-gray-500"
                          }`}
                        >
                          {TYPE_LABELS[method.payment_method] ||
                            method.payment_method}
                        </span>
                        {method.is_default && <DefaultBadge />}
                      </div>
                      {isOther && (
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {method.other_payment_detail?.payment_method_name || "—"}
                        </div>
                      )}
                    </td>

                    <td className="px-3 py-3 align-top text-gray-700">
                      <Details method={method} />
                    </td>

                    <td className="px-3 py-3 align-top text-xs whitespace-nowrap text-gray-500">
                      {formatDate(method.createdAt)}
                    </td>

                    <td className="px-3 py-3 align-top whitespace-nowrap">
                      <div className="flex gap-3">
                        <Pencil
                          className="w-4 h-4 text-brand-400 hover:text-brand-600 cursor-pointer transition-colors"
                          title="Edit"
                          onClick={() => onEdit?.(method)}
                        />
                        <Trash2
                          className="w-4 h-4 text-danger-400 hover:text-danger-600 cursor-pointer transition-colors"
                          title="Delete"
                          onClick={() => onDelete?.(method.id)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentMethodList;
